import React, { useState, useEffect } from "react";

const DepreciationCalculator = () => {
  const [assetCost, setAssetCost] = useState(50000);
  const [salvageValue, setSalvageValue] = useState(5000);
  const [depreciationMethod, setDepreciationMethod] = useState("straight-line");
  const [usefulLife, setUsefulLife] = useState(5);
  const [currentYear, setCurrentYear] = useState(1);
  const [currency, setCurrency] = useState("USD");
  const [productionUnits, setProductionUnits] = useState(100000);
  const [result, setResult] = useState({
    annualDepreciation: "0",
    accumulatedDepreciation: "0",
    bookValue: "0",
    depreciationRate: "0",
    breakdown: "",
  });

  // Depreciation Calculation
  const calculateDepreciation = () => {
    let cost = parseFloat(assetCost);
    let salvage = parseFloat(salvageValue);
    let life = parseFloat(usefulLife);
    let year = parseFloat(currentYear);
    let units = parseFloat(productionUnits);

    if (isNaN(cost) || cost <= 0 || isNaN(salvage) || salvage < 0 || isNaN(life) || life <= 0 || isNaN(year) || year <= 0 || year > life) return;

    let annualDep = 0;
    let accumulatedDep = 0;
    let depRate = 0;
    let methodDescription = "";

    const depreciableAmount = cost - salvage;

    switch(depreciationMethod) {
      case "straight-line":
        // Straight Line Method
        annualDep = depreciableAmount / life;
        accumulatedDep = annualDep * year;
        depRate = (1 / life) * 100;
        methodDescription = "Straight-Line method spreads the cost evenly over the asset's useful life. Annual depreciation remains constant each year.";
        break;

      case "declining-balance":
        // Declining Balance Method (Double Declining)
        depRate = (2 / life) * 100;
        let bookValue = cost;
        let totalDep = 0;
        
        for (let i = 1; i <= year; i++) {
          let yearDep = bookValue * (depRate / 100);
          if (bookValue - yearDep < salvage) {
            yearDep = bookValue - salvage;
          }
          if (i === year) {
            annualDep = yearDep;
          }
          totalDep += yearDep;
          bookValue -= yearDep;
        }
        accumulatedDep = totalDep;
        methodDescription = "Declining Balance (Double Declining) method accelerates depreciation with higher expenses in early years. Depreciation decreases over time.";
        break;

      case "sum-of-years":
        // Sum of Years Digits Method
        const sumOfYears = (life * (life + 1)) / 2;
        const remainingLife = life - year + 1;
        annualDep = (depreciableAmount * remainingLife) / sumOfYears;
        
        let accSum = 0;
        for (let i = 1; i <= year; i++) {
          const remLife = life - i + 1;
          accSum += (depreciableAmount * remLife) / sumOfYears;
        }
        accumulatedDep = accSum;
        depRate = (remainingLife / sumOfYears) * 100;
        methodDescription = "Sum-of-Years-Digits method provides accelerated depreciation. Each year's depreciation is based on remaining useful life divided by sum of all years.";
        break;

      case "units-of-production":
        // Units of Production Method
        if (isNaN(units) || units <= 0) {
          units = 100000;
        }
        const unitsThisYear = units / life; // Assuming even production
        annualDep = (depreciableAmount / units) * unitsThisYear;
        accumulatedDep = annualDep * year;
        depRate = (unitsThisYear / units) * 100;
        methodDescription = "Units-of-Production method bases depreciation on actual usage. Depreciation varies with production volume or hours used.";
        break;
    }

    const currentBookValue = cost - accumulatedDep;

    // Format currency symbol
    const currencySymbols = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      INR: "₹",
      JPY: "¥",
      AUD: "A$",
      CAD: "C$",
    };
    const symbol = currencySymbols[currency] || currency;

    const annualDepDisplay = `${symbol}${annualDep.toFixed(2)}`;
    const accumulatedDepDisplay = `${symbol}${accumulatedDep.toFixed(2)}`;
    const bookValueDisplay = `${symbol}${currentBookValue.toFixed(2)}`;
    const depRateDisplay = `${depRate.toFixed(2)}%`;

    let breakdown = `Using the ${depreciationMethod.replace('-', ' ').toUpperCase()} method for an asset worth ${symbol}${cost.toFixed(2)} with ${symbol}${salvage.toFixed(2)} salvage value over ${life} years. In year ${year}, the annual depreciation is ${annualDepDisplay}. Total accumulated depreciation is ${accumulatedDepDisplay}, leaving a book value of ${bookValueDisplay}. ${methodDescription}`;

    setResult({
      annualDepreciation: annualDepDisplay,
      accumulatedDepreciation: accumulatedDepDisplay,
      bookValue: bookValueDisplay,
      depreciationRate: depRateDisplay,
      breakdown,
    });
  };

  useEffect(() => {
    calculateDepreciation();
  }, []);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "white" }}>
              <line x1="12" y1="1" x2="12" y2="23"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              <polyline points="16 18 12 22 8 18"></polyline>
            </svg>
          </div>
          <h1>Depreciation Calculator</h1>
          <p>
            Calculate asset depreciation using multiple accounting methods including Straight-Line, Declining Balance, Sum-of-Years-Digits, and Units of Production. Get accurate annual depreciation, accumulated depreciation, and book value calculations.
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Asset Information
              </h2>

              {/* Asset Cost Input */}
              <div className="input-group">
                <label>Asset Cost ({currency})</label>
                <div className="input-wrapper">
                  <span className="input-prefix">💰</span>
                  <input
                    type="number"
                    value={assetCost}
                    onChange={(e) => setAssetCost(e.target.value)}
                    className="calc-input"
                    placeholder="50000"
                    min="0"
                    step="100"
                  />
                </div>
                <div className="quick-select-group">
                  {[10000, 25000, 50000, 100000, 250000].map((val) => (
                    <button
                      key={val}
                      onClick={() => setAssetCost(val)}
                      className="quick-select-btn primary"
                    >
                      {val >= 1000 ? `${val/1000}K` : val}
                    </button>
                  ))}
                </div>
              </div>

              {/* Salvage Value Input */}
              <div className="input-group">
                <label>Salvage Value ({currency})</label>
                <div className="input-wrapper">
                  <span className="input-prefix">💵</span>
                  <input
                    type="number"
                    value={salvageValue}
                    onChange={(e) => setSalvageValue(e.target.value)}
                    className="calc-input"
                    placeholder="5000"
                    min="0"
                    step="100"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 1000, 5000, 10000, 20000].map((h) => (
                    <button
                      key={h}
                      onClick={() => setSalvageValue(h)}
                      className="quick-select-btn success"
                    >
                      {h >= 1000 ? `${h/1000}K` : h}
                    </button>
                  ))}
                </div>
              </div>

              {/* Depreciation Method */}
              <div className="input-group">
                <label>Depreciation Method</label>
                <div className="quick-select-group">
                  {[
                    { value: "straight-line", label: "Straight Line" },
                    { value: "declining-balance", label: "Declining Balance" },
                  ].map((method) => (
                    <button
                      key={method.value}
                      onClick={() => setDepreciationMethod(method.value)}
                      className={`quick-select-btn ${depreciationMethod === method.value ? "primary" : "secondary"}`}
                    >
                      {method.label}
                    </button>
                  ))}
                </div>
                <div className="quick-select-group" style={{ marginTop: "0.5rem" }}>
                  {[
                    { value: "sum-of-years", label: "Sum-of-Years" },
                    { value: "units-of-production", label: "Units Production" },
                  ].map((method) => (
                    <button
                      key={method.value}
                      onClick={() => setDepreciationMethod(method.value)}
                      className={`quick-select-btn ${depreciationMethod === method.value ? "primary" : "secondary"}`}
                    >
                      {method.label}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.5rem" }}>
                  Different methods affect tax deductions and financial reporting. Consult with an accountant for your specific situation.
                </p>
              </div>

              {/* Useful Life */}
              <div className="input-group">
                <label>Useful Life (Years)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📅</span>
                  <input
                    type="number"
                    value={usefulLife}
                    onChange={(e) => setUsefulLife(e.target.value)}
                    className="calc-input"
                    placeholder="5"
                    min="1"
                  />
                </div>
                <div className="quick-select-group">
                  {[3, 5, 7, 10, 15].map((val) => (
                    <button
                      key={val}
                      onClick={() => setUsefulLife(val)}
                      className="quick-select-btn success"
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>

              {/* Current Year */}
              <div className="input-group">
                <label>Current Year</label>
                <div className="input-wrapper">
                  <span className="input-prefix">🔢</span>
                  <input
                    type="number"
                    value={currentYear}
                    onChange={(e) => setCurrentYear(e.target.value)}
                    className="calc-input"
                    placeholder="1"
                    min="1"
                    max={usefulLife}
                  />
                </div>
                <div className="quick-select-group">
                  {[1, 2, 3, 4, 5].filter(y => y <= usefulLife).map((val) => (
                    <button
                      key={val}
                      onClick={() => setCurrentYear(val)}
                      className="quick-select-btn primary"
                    >
                      Year {val}
                    </button>
                  ))}
                </div>
              </div>

              {/* Production Units (for units-of-production method) */}
              {depreciationMethod === "units-of-production" && (
                <div className="input-group">
                  <label>Total Production Units/Hours</label>
                  <div className="input-wrapper">
                    <span className="input-prefix">⚙️</span>
                    <input
                      type="number"
                      value={productionUnits}
                      onChange={(e) => setProductionUnits(e.target.value)}
                      className="calc-input"
                      placeholder="100000"
                      min="1"
                    />
                  </div>
                  <div className="quick-select-group">
                    {[50000, 100000, 200000, 500000, 1000000].map((val) => (
                      <button
                        key={val}
                        onClick={() => setProductionUnits(val)}
                        className="quick-select-btn success"
                      >
                        {val >= 1000 ? `${val/1000}K` : val}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Currency */}
              <div className="input-group">
                <label>Currency</label>
                <div className="quick-select-group">
                  {[
                    { value: "USD", label: "USD ($)" },
                    { value: "EUR", label: "EUR (€)" },
                    { value: "GBP", label: "GBP (£)" },
                    { value: "INR", label: "INR (₹)" },
                  ].map((curr) => (
                    <button
                      key={curr.value}
                      onClick={() => setCurrency(curr.value)}
                      className={`quick-select-btn ${currency === curr.value ? "primary" : "secondary"}`}
                    >
                      {curr.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateDepreciation} className="calculate-btn">
                Calculate Depreciation
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 8v4l3 2"></path>
                </svg>
                Depreciation Results
              </h2>

              <div className="result-card">
                {/* Annual Depreciation */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Annual Depreciation</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.annualDepreciation}</div>
                  <div className="result-subtext">year {currentYear} expense</div>
                </div>

                {/* Accumulated Depreciation */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Accumulated Depreciation</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.accumulatedDepreciation}</div>
                  <div className="result-subtext">total through year {currentYear}</div>
                </div>

                {/* Book Value */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Current Book Value</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.bookValue}</div>
                  <div className="result-subtext">remaining asset value</div>
                </div>

                {/* Depreciation Rate */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Depreciation Rate</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="8 12 12 16 16 12"></polyline>
                        <line x1="12" y1="8" x2="12" y2="16"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.depreciationRate}</div>
                  <div className="result-subtext">current year rate</div>
                </div>

                {/* Breakdown */}
                <div className="breakdown-section" style={{ marginTop: "1rem" }}>
                  <h3 className="breakdown-title">Calculation Explanation</h3>
                  <p style={{ fontSize: "0.9rem", color: "#065f46" }}>
                    {result.breakdown}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Asset Depreciation</h3>
              <p>
                Depreciation is the systematic allocation of an asset's cost over its useful life. It reflects the decline in value due to wear and tear, obsolescence, or age. Proper depreciation accounting is essential for accurate financial statements, tax deductions, and business decision-making.
                Different depreciation methods are suited for different asset types and business needs. Straight-line is simple and commonly used, while accelerated methods like declining balance provide larger tax deductions in early years. Choose the method that best matches your asset's actual value decline pattern.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Depreciation Methods Explained:</h4>
            <div className="formula-display">
              Straight-Line: (Cost - Salvage Value) ÷ Useful Life
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Declining Balance: Book Value × (2 ÷ Useful Life)
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Sum-of-Years-Digits: Depreciable Amount × (Remaining Life ÷ Sum of Years)
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Units of Production: (Cost - Salvage) × (Units Used ÷ Total Units)
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Depreciable Amount</div>
                <div className="formula-param-value">Cost - Salvage Value</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Book Value</div>
                <div className="formula-param-value">Cost - Accumulated Depreciation</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Salvage Value</div>
                <div className="formula-param-value">Estimated resale value</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Useful Life</div>
                <div className="formula-param-value">Expected years of use</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>Which depreciation method is best?</h5>
              <p>
                Straight-line is simplest and most common. Accelerated methods (declining balance, sum-of-years) provide larger early deductions, useful for tax planning or assets that lose value quickly like technology.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is salvage value?</h5>
              <p>
                Salvage value (residual value) is the estimated amount you can sell the asset for at the end of its useful life. It reduces the total amount to be depreciated over the asset's lifetime.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can I change depreciation methods?</h5>
              <p>
                Generally, you must use the same method consistently. Changing methods requires justification and approval from tax authorities. Consult an accountant before making changes.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is book value?</h5>
              <p>
                Book value is the asset's net value on your balance sheet: original cost minus accumulated depreciation. It represents the remaining undepreciated portion of the asset's cost.
              </p>
            </div>

            <div className="faq-item">
              <h5>How do I determine useful life?</h5>
              <p>
                Useful life depends on the asset type and usage. Tax authorities provide guidelines (e.g., IRS Publication 946). Consider manufacturer specifications, industry standards, and your specific usage patterns.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepreciationCalculator;